// src/features/pages/admin/UsersRolesPage.tsx
// Phase 3: company-admin Users & Roles. Live against auth-service /users
// (tenant-scoped server-side). List users, reassign roles, activate/deactivate.
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm, usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import toast from 'react-hot-toast';
import { Users, Search, Loader2, UserCheck, UserX, UserPlus, X, ShieldOff, Unlock, Mail, MailCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DuplicatePersonWarning, SharedMailboxWarning } from '@/features/shared/components/DuplicatePersonWarning';
import { listUsers, updateUserRoles, setUserActive, inviteUser, adminResetUserMfa, adminUnlockUser, getAllEmployees, listInvitations, resendInvitation, cancelInvitation, type AdminUser, type InviteUserInput, type Employee, type PendingInvitation, checkDuplicatePerson, type DuplicateMatch } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { RBAC, normalizeRoles } from '@/rbac/rbac.generated';
import { useHasPermission } from '@/rbac/useRbac';
import { RequirePermission } from '@/rbac/RequirePermission';

// Roles a company admin can assign to their OWN staff: company scopes only.
// Excludes both platform roles (the KaziPeople console) AND partner roles (the
// Partner Portal for external lenders) — those are managed in their own apps and
// must never appear in a tenant's Company Users list.
const COMPANY_SCOPES = new Set(['tenant', 'team', 'self']);
const ASSIGNABLE = Object.entries(
  RBAC.roles as Record<string, { scope: string; label: string }>,
)
  .filter(([, def]) => COMPANY_SCOPES.has(def.scope))
  .map(([key, def]) => ({ key, label: def.label }));
const ASSIGNABLE_KEYS = ASSIGNABLE.map((r) => r.key);

// Roles the invitation endpoint accepts (DTO enum), with friendly label keys.
const INVITE_ROLES: { value: InviteUserInput['role']; labelKey: string }[] = [
  { value: 'employee', labelKey: 'users.role_employee' },
  { value: 'manager', labelKey: 'users.role_manager' },
  { value: 'hr', labelKey: 'users.role_hr' },
  { value: 'finance', labelKey: 'users.role_finance' },
  { value: 'admin', labelKey: 'users.role_admin' },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls =
  'mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400';
const labelFor = (key: string) =>
  (RBAC.roles as Record<string, { label?: string }>)[key]?.label ?? key;

const displayName = (u: AdminUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;
const initials = (u: AdminUser) =>
  displayName(u).split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
/** The user's current primary assignable role (canonical). */
const primaryRole = (u: AdminUser) =>
  normalizeRoles(u.roles || []).find((r) => ASSIGNABLE_KEYS.includes(r)) || 'employee';

// A unified row: either a login account (with a role) or an employee record that
// has no account yet (invite-able).
type Row =
  | { kind: 'user'; key: string; name: string; email: string; u: AdminUser }
  | { kind: 'employee'; key: string; name: string; email: string; department?: string };

const UsersRolesInner: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const promptText = usePrompt();
  const canAssign = useHasPermission('user:role:assign');
  const canDeactivate = useHasPermission('user:deactivate');
  const canInvite = useHasPermission('user:create');
  const canResetMfa = useHasPermission('user:mfa:reset');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  // Per-row chosen role for inviting a no-account employee.
  const [inviteRole, setInviteRole] = useState<Record<string, string>>({});

  // Pending (not-yet-accepted) invitations — so admins can see and RESEND
  // invites that never arrived instead of guessing.
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Invite form
  const [inviteOpen, setInviteOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState<InviteUserInput & { firstName?: string; lastName?: string }>({ email: '', name: '', firstName: '', lastName: '', role: 'employee', department: '' });

  const load = async () => {
    setLoading(true);
    try {
      // Login accounts (roles) + ALL employees (incl. imported, no-account).
      const [res, emps] = await Promise.all([
        listUsers({ page: 1, limit: 200 }),
        getAllEmployees().catch(() => [] as Employee[]),
      ]);
      setUsers(Array.isArray(res?.users) ? res.users : []);
      setEmployees(Array.isArray(emps) ? emps : []);
      // Best-effort: an invitations failure must not break the users list.
      listInvitations()
        .then((inv) => setInvitations(Array.isArray(inv) ? inv : []))
        .catch(() => setInvitations([]));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('users.load_error')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  // Invitations still awaiting acceptance (excluding cancelled ones).
  const pendingInvites = useMemo(
    () => invitations.filter((i) => !i.acceptedAt && i.metadata?.status !== 'cancelled'),
    [invitations],
  );

  // SCRUM-20: rows whose email already has a pending invite show an "Invited"
  // state instead of the orange button, so nobody sends twice. justInvited is
  // the optimistic overlay for the moment right after a send (before the
  // invitations refetch lands).
  const [justInvited, setJustInvited] = useState<Set<string>>(new Set());
  const invitedEmails = useMemo(() => {
    const s = new Set(pendingInvites.map((i) => (i.email || '').toLowerCase()));
    justInvited.forEach((e) => s.add(e));
    return s;
  }, [pendingInvites, justInvited]);

  // Merge: every login account, plus employees who don't have an account yet
  // (matched by email) so HR can see and invite the whole company from one place.
  const rows = useMemo<Row[]>(() => {
    const accountEmails = new Set(users.map((u) => (u.email || '').toLowerCase()).filter(Boolean));
    const userRows: Row[] = users.map((u) => ({ kind: 'user', key: u.id, name: displayName(u), email: u.email, u }));
    const empRows: Row[] = employees
      .filter((e) => e.email && !accountEmails.has(e.email.toLowerCase()))
      .map((e) => ({
        kind: 'employee',
        key: `emp-${e.id}`,
        name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email,
        email: e.email,
        // The employees API returns department as a RELATION OBJECT; the invite
        // DTO wants a plain string — send the name (a raw object 400s).
        department:
          typeof (e as any).department === 'string'
            ? (e as any).department
            : ((e as any).department?.name ?? undefined),
      }));
    return [...userRows, ...empRows];
  }, [users, employees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  }, [rows, search]);

  // Bulk invite: select several no-login employees, one send, per-person
  // results — so "did everyone get theirs?" has a definite answer.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);

  const inviteSelected = async (rowsToInvite: Row[]) => {
    setBulkSending(true);
    const failed: string[] = [];
    let sent = 0;
    // Sequential on purpose: deterministic order, no rate-limit bursts, and a
    // single failure never aborts the rest.
    for (const r of rowsToInvite) {
      try {
        const role = (inviteRole[r.key] || 'employee') as InviteUserInput['role'];
        await inviteUser({ email: r.email, name: r.name, role, department: r.department });
        sent += 1;
        setJustInvited((p) => new Set(p).add(r.email.toLowerCase()));
        setSelected((p) => { const n = new Set(p); n.delete(r.key); return n; });
      } catch {
        failed.push(r.name || r.email);
      }
    }
    setBulkSending(false);
    if (failed.length === 0) {
      toast.success(t('users.bulk_invited', { count: sent, defaultValue: `${sent} invitation${sent === 1 ? '' : 's'} sent` }));
    } else {
      toast.error(t('users.bulk_invite_partial', {
        sent, failedList: failed.join(', '),
        defaultValue: `${sent} sent — failed for: ${failed.join(', ')}. Try those again.`,
      }), { duration: 8000 });
    }
    void load();
  };

  const inviteEmployee = async (r: Row) => {
    if (r.kind !== 'employee') return;
    const role = inviteRole[r.key] || 'employee';
    setBusyId(r.key);
    try {
      await inviteUser({ email: r.email, name: r.name, role: role as InviteUserInput['role'], department: r.department });
      toast.success(t('users.invited', { name: r.name, defaultValue: `Invitation sent to ${r.name}` }));
      setJustInvited((p) => new Set(p).add(r.email.toLowerCase()));
      void load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('users.invitation_error')));
    } finally {
      setBusyId(null);
    }
  };

  const changeRole = async (u: AdminUser, role: string) => {
    if (role === primaryRole(u)) return;
    const ok = await confirm({
      title: t('users.change_role_title', { defaultValue: 'Change user type' }),
      message: t('users.change_role_confirm', {
        name: displayName(u),
        role: labelFor(role),
        defaultValue: `Change ${displayName(u)}'s user type to ${labelFor(role)}? They'll be signed out and emailed to sign back in with the new access.`,
      }),
      confirmLabel: t('users.change_role_cta', { defaultValue: 'Change user type' }),
    });
    if (!ok) return;
    const roles = role === 'employee' ? ['employee'] : [role, 'employee'];
    setBusyId(u.id);
    try {
      await updateUserRoles(u.id, roles);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, roles } : x)));
      toast.success(t('users.now_role', { name: displayName(u), role: labelFor(role) }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('users.role_error')), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (u: AdminUser) => {
    const next = !(u.isActive ?? true);
    setBusyId(u.id);
    try {
      await setUserActive(u.id, next);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isActive: next } : x)));
      toast.success(next ? t('users.status_activated', { name: displayName(u) }) : t('users.status_deactivated', { name: displayName(u) }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('users.status_error')), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  const resetMfa = async (u: AdminUser) => {
    if (!(await confirm({ title: 'Reset two-factor', message: t('users.reset_mfa_confirm', { name: displayName(u), defaultValue: 'Reset two-factor authentication for {{name}}? They will be able to sign in without a code and can set it up again.' }), confirmLabel: 'Reset', danger: true }))) return;
    setBusyId(u.id);
    try {
      await adminResetUserMfa(u.id);
      toast.success(t('users.reset_mfa_done', { name: displayName(u), defaultValue: '2FA reset for {{name}}' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('users.reset_mfa_error', { defaultValue: 'Could not reset 2FA' })));
    } finally {
      setBusyId(null);
    }
  };

  const unlock = async (u: AdminUser) => {
    setBusyId(u.id);
    try {
      await adminUnlockUser(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, lockedUntil: null, status: 'active' } : x)));
      toast.success(t('users.unlock_done', { name: displayName(u), defaultValue: 'Unlocked {{name}} — they can sign in now' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('users.unlock_error', { defaultValue: 'Could not unlock the account' })), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  // Look for somebody already here as the invite is typed. Debounced because it
  // runs on every keystroke; fails soft because a duplicate CHECK that errors
  // must never be the reason an invitation cannot be sent.
  const [dupes, setDupes] = useState<DuplicateMatch[]>([]);
  const [sharedAddress, setSharedAddress] = useState(false);
  useEffect(() => {
    const email = invite.email.trim();
    const first = (invite.firstName || '').trim();
    const last = (invite.lastName || '').trim();
    if (!inviteOpen || (!EMAIL_RE.test(email) && !(first || last))) {
      setDupes([]);
      setSharedAddress(false);
      return;
    }
    const id = setTimeout(() => {
      checkDuplicatePerson({
        email: EMAIL_RE.test(email) ? email : undefined,
        first_name: first || undefined,
        last_name: last || undefined,
      }).then((r) => { setDupes(r.matches); setSharedAddress(r.sharedAddress); });
    }, 400);
    return () => clearTimeout(id);
  }, [inviteOpen, invite.email, invite.firstName, invite.lastName]);

  const sendInvite = async () => {
    if (!EMAIL_RE.test(invite.email.trim())) {
      toast.error(t('users.invalid_email'));
      return;
    }
    setSending(true);
    try {
      const composedName = `${invite.firstName || ''} ${invite.lastName || ''}`.replace(/\s+/g, ' ').trim();
      await inviteUser({
        email: invite.email.trim(),
        name: composedName || undefined,
        role: invite.role,
        department: invite.department?.trim() || undefined,
      });
      toast.success(t('users.invitation_sent', { email: invite.email.trim() }));
      setInvite({ email: '', name: '', firstName: '', lastName: '', role: 'employee', department: '' });
      setDupes([]);
      setSharedAddress(false);
      setInviteOpen(false);
    } catch (e: any) {
      // Surface the backend's business message (e.g. "An invitation for this email
      // is already pending.") instead of a generic failure.
      toast.error(getApiErrorMessage(e, t('users.invitation_error')));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-charcoal">{t('users.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('users.subtitle')}</p>
        </div>
        {canInvite && !inviteOpen && (
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setInviteOpen(true)}>
            <UserPlus className="w-4 h-4 mr-1" /> {t('users.invite')}
          </Button>
        )}
      </div>

      {pendingInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {t('users.pending_invites', { defaultValue: 'Pending invitations' })}
              <span className="text-xs font-normal text-muted-foreground">
                {t('users.pending_invites_hint', { defaultValue: "Sent but not yet accepted — resend if someone didn't receive theirs." })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {pendingInvites.map((inv) => {
              const expired = inv.expiresAt ? new Date(inv.expiresAt).getTime() < Date.now() : false;
              return (
                <div key={inv.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{inv.name || inv.email}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {inv.email}{inv.role ? ` · ${inv.role}` : ''}
                      {inv.metadata?.resentAt
                        ? ` · ${t('users.resent', { defaultValue: 'resent' })} ${formatDate(inv.metadata.resentAt)}`
                        : inv.createdAt ? ` · ${t('users.sent', { defaultValue: 'sent' })} ${formatDate(inv.createdAt)}` : ''}
                    </p>
                  </div>
                  {expired && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-danger-soft text-danger-strong">
                      {t('users.invite_expired', { defaultValue: 'Expired' })}
                    </span>
                  )}
                  {canInvite && inv.invitationToken && (
                    <Button
                      size="sm"
                      variant="outline"
                      title={t('users.copy_link_hint', { defaultValue: 'Same link as the email — share it directly (WhatsApp, SMS) if their email is unreliable.' })}
                      onClick={async () => {
                        const url = `${window.location.origin}/invite/accept?token=${inv.invitationToken}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success(t('users.invite_link_copied', { defaultValue: 'Invite link copied — share it with {{email}}.', email: inv.email }));
                        } catch {
                          await promptText({ title: t('users.copy_link', { defaultValue: 'Copy link' }), label: t('users.copy_manually', { defaultValue: 'Copy this invite link:' }), defaultValue: url, confirmLabel: t('common.done', { defaultValue: 'Done' }) });
                        }
                      }}
                    >
                      {t('users.copy_link', { defaultValue: 'Copy link' })}
                    </Button>
                  )}
                  {canInvite && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resendingId === inv.id}
                        onClick={async () => {
                          setResendingId(inv.id);
                          try {
                            await resendInvitation(inv.id);
                            toast.success(t('users.invite_resent', { defaultValue: 'Invitation resent to {{email}}.', email: inv.email }));
                            void load();
                          } catch (e: any) {
                            toast.error(getApiErrorMessage(e, t('users.invite_resend_failed', { defaultValue: 'Could not resend the invitation.' })));
                          } finally {
                            setResendingId(null);
                          }
                        }}
                      >
                        {resendingId === inv.id
                          ? t('users.resending', { defaultValue: 'Resending…' })
                          : t('users.resend_invite', { defaultValue: 'Resend invite' })}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={async () => {
                          const ok = await confirm({
                            title: t('users.cancel_invite', { defaultValue: 'Cancel invitation' }),
                            message: t('users.cancel_invite_desc', { defaultValue: 'The invite link for {{email}} will stop working.', email: inv.email }),
                            confirmLabel: t('users.cancel_invite', { defaultValue: 'Cancel invitation' }),
                            cancelLabel: t('common.keep', { defaultValue: 'Keep' }),
                            danger: true,
                          });
                          if (!ok) return;
                          try {
                            await cancelInvitation(inv.id);
                            toast.success(t('users.invite_cancelled', { defaultValue: 'Invitation cancelled.' }));
                            void load();
                          } catch (e: any) {
                            toast.error(getApiErrorMessage(e, t('users.invite_cancel_failed', { defaultValue: 'Could not cancel the invitation.' })));
                          }
                        }}
                      >
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {inviteOpen && (
        <Card className="border-orange-200 dark:border-orange-500/30">
          <CardHeader className="rounded-t-xl flex-row items-center">
            <CardTitle className="flex-1 text-orange-700">{t('users.invite_title')}</CardTitle>
            <button type="button" onClick={() => setInviteOpen(false)} aria-label={t('common.close')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="inv-email">{t('users.email')}</label>
              <input id="inv-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder={t('users.placeholder_email')} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="inv-first">{t('common.first_name', { defaultValue: 'First name' })}</label>
                <input id="inv-first" value={invite.firstName} onChange={(e) => setInvite({ ...invite, firstName: e.target.value })}
                  placeholder={t('common.optional')} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="inv-last">{t('common.last_name', { defaultValue: 'Last name' })}</label>
                <input id="inv-last" value={invite.lastName} onChange={(e) => setInvite({ ...invite, lastName: e.target.value })}
                  placeholder={t('common.optional')} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="inv-role">{t('users.role')}</label>
              <select id="inv-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as InviteUserInput['role'] })}
                className={inputCls}>
                {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{t(r.labelKey)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="inv-dept">{t('users.department')}</label>
              <input id="inv-dept" value={invite.department} onChange={(e) => setInvite({ ...invite, department: e.target.value })}
                placeholder={t('common.optional')} className={inputCls} />
            </div>
            {sharedAddress && (
              <div className="sm:col-span-2"><SharedMailboxWarning /></div>
            )}
            {dupes.length > 0 && (
              <div className="sm:col-span-2">
                <DuplicatePersonWarning matches={dupes}>
                  <p className="text-xs text-muted-foreground">
                    {t('users.dup_invite_anyway', {
                      defaultValue: 'You can still send this invitation — check it is not the same person first.',
                    })}
                  </p>
                </DuplicatePersonWarning>
              </div>
            )}
            <div className="sm:col-span-2 flex items-center gap-2">
              {/* Never disabled by a duplicate match: two people can share a
                  name, and blocking the second one stops somebody being paid. */}
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={sendInvite} disabled={sending}>
                {sending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {t('users.send_invitation')}
              </Button>
              <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={sending}>{t('common.cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardHeader className="rounded-t-xl flex-row items-center gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-orange-700 flex-1">
            <Users className="w-5 h-5" /> {t('users.company_users')}
          </CardTitle>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-cream border border-border min-w-[200px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('users.search_placeholder')}
              className="bg-transparent outline-none text-sm w-full text-foreground"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {!loading && rows.length > 0 && (
            <p className="text-sm text-muted-foreground mb-4">
              <span className="font-medium text-foreground">{rows.filter((r) => r.kind === 'user').length}</span>{' '}
              {t('users.with_login', { defaultValue: 'with login' })}
              {rows.some((r) => r.kind === 'employee') && (
                <>
                  {' · '}
                  <span className="font-medium text-orange-600">{rows.filter((r) => r.kind === 'employee').length}</span>{' '}
                  {t('users.no_login_yet', { defaultValue: 'without login yet' })}
                </>
              )}
            </p>
          )}
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{t('users.empty')}</div>
          ) : (
            <>
            {(() => {
              const invitable = filtered.filter(
                (r) => r.kind === 'employee' && !invitedEmails.has(r.email.toLowerCase()));
              if (!canInvite || invitable.length < 2) return null;
              const chosen = invitable.filter((r) => selected.has(r.key));
              const allChosen = chosen.length === invitable.length;
              return (
                <div className="mb-3 flex items-center gap-3 flex-wrap rounded-[10px] border border-border bg-muted/40 px-3 py-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-orange-500"
                      checked={allChosen}
                      onChange={(e) => setSelected(e.target.checked ? new Set(invitable.map((r) => r.key)) : new Set())}
                    />
                    {t('users.select_all_invitable', {
                      count: invitable.length,
                      defaultValue: `Select all without login (${invitable.length})`,
                    })}
                  </label>
                  <button
                    type="button"
                    disabled={chosen.length === 0 || bulkSending}
                    onClick={() => inviteSelected(chosen)}
                    className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-[10px] bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                  >
                    {bulkSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    {t('users.invite_selected', {
                      count: chosen.length,
                      defaultValue: `Send ${chosen.length || ''} invite${chosen.length === 1 ? '' : 's'}`,
                    })}
                  </button>
                </div>
              );
            })()}
            <ul className="divide-y divide-border">
              {filtered.map((r) => {
                // No-account employee (e.g. imported) — show an Invite action so HR
                // can grant them a login + role without leaving this page.
                if (r.kind === 'employee') {
                  const nameInitials = r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                  const invitableRow = canInvite && !invitedEmails.has(r.email.toLowerCase());
                  return (
                    <li key={r.key} className="py-3 flex items-center gap-3 flex-wrap">
                      {invitableRow && (
                        <input
                          type="checkbox"
                          aria-label={t('users.select_for_invite', { name: r.name, defaultValue: `Select ${r.name} for invite` })}
                          className="h-4 w-4 accent-orange-500"
                          checked={selected.has(r.key)}
                          onChange={(e) => setSelected((p) => {
                            const n = new Set(p);
                            if (e.target.checked) n.add(r.key); else n.delete(r.key);
                            return n;
                          })}
                        />
                      )}
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-muted text-muted-foreground font-semibold text-sm">
                        {nameInitials}
                      </span>
                      <div className="flex-1 min-w-[160px]">
                        <div className="font-medium text-foreground flex items-center gap-2">
                          {r.name}
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('users.no_login', { defaultValue: 'No login yet' })}</span>
                        </div>
                        <div className="text-sm text-muted-foreground truncate">{r.email}{r.department ? ` · ${r.department}` : ''}</div>
                      </div>
                      {canInvite && invitedEmails.has(r.email.toLowerCase()) && (
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.15] dark:text-emerald-300"
                          title={t('users.invited_hint', { defaultValue: 'Invitation already sent — resend from the pending invitations list above if needed.' })}
                        >
                          <MailCheck className="w-3.5 h-3.5" />
                          {t('users.invited_state', { defaultValue: 'Invited' })}
                        </span>
                      )}
                      {canInvite && !invitedEmails.has(r.email.toLowerCase()) && (
                        <>
                          <select
                            aria-label={t('users.invite_role_for', { name: r.name, defaultValue: `Role for ${r.name}` })}
                            value={inviteRole[r.key] || 'employee'}
                            onChange={(e) => setInviteRole((p) => ({ ...p, [r.key]: e.target.value }))}
                            className="text-sm rounded-[10px] border border-border bg-cream text-foreground px-2.5 py-1.5"
                          >
                            {/* Invite accepts only the coarse roles (CreateInvitationDto UserRole:
                                admin|manager|employee|hr|finance). Finer RBAC roles (hr_manager,
                                hr_staff…) are set AFTER login via the change-role dropdown. */}
                            {INVITE_ROLES.map((rr) => <option key={rr.value} value={rr.value}>{t(rr.labelKey)}</option>)}
                          </select>
                          <button
                            type="button"
                            onClick={() => inviteEmployee(r)}
                            disabled={busyId === r.key}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-[10px] bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60"
                          >
                            {busyId === r.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                            {t('users.invite_action', { defaultValue: 'Invite' })}
                          </button>
                        </>
                      )}
                    </li>
                  );
                }
                const u = r.u;
                const active = u.isActive ?? true;
                return (
                  <li key={u.id} className="py-3 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 font-semibold text-sm">
                      {initials(u)}
                    </span>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {displayName(u)}
                        {!active && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('common.inactive')}</span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">{u.email}</div>
                      {/* Custom roles (e.g. Chairman) — distinct from the base User
                          Type. Managed in Roles & Permissions → Assignments. */}
                      {Array.isArray(u.customRoles) && u.customRoles.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {u.customRoles.map((cr) => (
                            <span
                              key={cr.id}
                              className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-500/[0.15] dark:text-orange-300"
                              title={t('users.custom_role', { defaultValue: 'Custom role' })}
                            >
                              {cr.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-start gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('users.user_type', { defaultValue: 'User Type' })}
                      </span>
                      <select
                        aria-label={t('users.user_type_for', { name: displayName(u), defaultValue: `User Type for ${displayName(u)}` })}
                        value={primaryRole(u)}
                        disabled={!canAssign || busyId === u.id}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className="text-sm rounded-[10px] border border-border bg-cream text-foreground px-2.5 py-1.5 disabled:opacity-60"
                      >
                        {ASSIGNABLE.map((r) => (
                          <option key={r.key} value={r.key}>{r.label}</option>
                        ))}
                      </select>
                    </div>

                    {canDeactivate && (
                      <button
                        type="button"
                        onClick={() => toggleActive(u)}
                        disabled={busyId === u.id}
                        title={active ? t('users.deactivate') : t('users.activate')}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border transition-colors disabled:opacity-60 ${
                          active
                            ? 'border-border text-muted-foreground hover:text-danger hover:border-danger'
                            : 'border-success text-success hover:bg-green-50'
                        }`}
                      >
                        {active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                        {active ? t('users.deactivate') : t('users.activate')}
                      </button>
                    )}

                    {canDeactivate && (u.status === 'locked' || (u.lockedUntil && new Date(u.lockedUntil).getTime() > Date.now())) && (
                      <button
                        type="button"
                        onClick={() => unlock(u)}
                        disabled={busyId === u.id}
                        title={t('users.unlock', { defaultValue: 'Unlock account' })}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60"
                      >
                        <Unlock className="w-3.5 h-3.5" />
                        {t('users.unlock', { defaultValue: 'Unlock' })}
                      </button>
                    )}

                    {canResetMfa && (
                      <button
                        type="button"
                        onClick={() => resetMfa(u)}
                        disabled={busyId === u.id}
                        title={t('users.reset_mfa', { defaultValue: 'Reset 2FA' })}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border border-border text-muted-foreground hover:text-orange-600 hover:border-orange-200 transition-colors disabled:opacity-60"
                      >
                        <ShieldOff className="w-3.5 h-3.5" />
                        {t('users.reset_mfa', { defaultValue: 'Reset 2FA' })}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const UsersRolesPage: React.FC = () => (
  // Viewable by anyone who can read company users (tenant_owner + hr_manager);
  // role reassignment, invite, and deactivate are gated individually inside.
  <RequirePermission perm="user:read:own-tenant">
    <UsersRolesInner />
  </RequirePermission>
);

export default UsersRolesPage;
